import seedrandom from 'seedrandom';
import shadowConfig from '../config';
import config from '../config';
import dayjs from "dayjs";
import tinycolor from "tinycolor2";
import queryString from "query-string";
import {Room} from "./stores/RoomStore";

function randomInt(bottom: number, top: number): number {
    return bottom + Math.floor(Math.random() * (top - bottom));
}

function getRandomLc() {
    const lower = 'a'.charCodeAt(0);
    const upper = 'z'.charCodeAt(0);
    return String.fromCharCode(randomInt(lower, upper));
}

function getRandomUc() {
    const lower = 'A'.charCodeAt(0);
    const upper = 'Z'.charCodeAt(0);
    return String.fromCharCode(randomInt(lower, upper));
}

export function createRandomString(start: number, end: number) {
    const size = start + randomInt(start, end);
    const stringArray = new Array(size).fill(0);
    return stringArray.map(() => Math.random() > 0.5 ? getRandomLc() : getRandomUc()).join("");
}

export function ensureVar(object: any, name: string, type: string = "string"): any {
    const value = object[name];
    if (value === undefined) {
        if (process.env.APP_ENV) {
            return value;
        }
        throw new Error(`undefined for required env var ${name}:  ${value}`)
    }

    switch (type) {
        case "string":
            return value;
        case "integer":
            return parseInt(value);
        case "boolean":
            return "true" === value;
        default:
            throw new Error("unknown type: " + type)
    }
}

export function sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function blankIfNonValue(value: any, prefix = '') {
    return value === null || value === undefined ? '' : prefix + value;
}

export function formatTimeLeft(ms: number): string {
    const duration = dayjs.duration(ms);
    const days = dayjs().add(duration).diff(dayjs(), "days");
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    const milliseconds = duration.milliseconds();

    const daysMessage = days + " days ";
    const hoursMessage = hours + " hours ";
    const minutesMessage = minutes + " minutes ";
    const secondsMessage = seconds + "." + milliseconds + " seconds ";
    const ending = "left till expiration";

    let result = "";

    if (days) {
        result += daysMessage;
    }

    if (hours) {
        result += hoursMessage;
    }

    if (minutes) {
        result += minutesMessage;
    }
    result += secondsMessage + ending;


    return result;

}

function throttle(callback: (x: any) => any, limit?: number) {
    let waiting = false;
    return function () {
        if (!waiting) {
            callback.apply(this, arguments);
            waiting = true;
            setTimeout(function () {
                waiting = false;
            }, limit);
        }
    }
}

export function deNormalize(object: any, ...subKeys: any[]) {
    if (!object) {
        return false;
    }
    return Object.keys(object).map((key: string) => {
        let value: any = object[key];
        subKeys.forEach(subKey => {
            value = value[subKey];
        });
        return value;
    });
}

export function normalize<T>(array: T[], key: keyof T) {
    return createObjectWithArrayValues(array, key)
}

export function createObjectWithArrayValues<T>(array: T[], key: keyof T): Record<keyof any, T> {
    let object: Record<keyof any, T> = {};
    array.forEach(value => {
        object[value[key] as keyof any] = value;
    });
    return object;
}

export function sortArrayObjects(array: any[], key: string, order = 'asc', priorityKey: any = null) {
    return array.sort((a, b) => {
        if (priorityKey && a[priorityKey]) {
            return -1;
        }

        if (priorityKey && b[priorityKey]) {
            return 1;
        }

        if (a[key] === null || a[key] < b[key]) {
            return order === 'asc' ? -1 : 1;
        } else if (b[key] === null || a[key] > b[key]) {
            return order === 'asc' ? 1 : -1;
        }
        return 0;
    });
}


export function shuffleArray(array: any[], seed: string) {
    const rng = seedrandom(seed);
    for (let i = array.length - 1; i > 0; i--) {
        let j = Math.floor(rng() * (i + 1));
        let temp = array[i];
        array[i] = array[j];
        array[j] = temp;
    }
    return array;
}

export function downloadStringAsFile(str: string, fileName: string) {
    const url = window.URL.createObjectURL(new Blob([str], {
        type: 'data:application/octet-stream'
    }));
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.style.display = "none";
    a.href = url;
    a.download = fileName;
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

export function readTextInFile(file: Blob): Promise<string> {
    const reader = new FileReader();
    reader.readAsText(file);
    return new Promise((resolve, reject) => {
        reader.onload = function () {
            if (reader.result && typeof reader.result === 'string') {
                try {
                    resolve(reader.result);
                } catch (e) {
                    reject(e);
                }
            } else {
                reject(new Error('File was loaded but no textual content found'));
            }
        };

        reader.onerror = function () {
            reject(new Error("Failed to read file!\n\n" + reader.error));
        };

        reader.onabort = function () {
            reject(new Error("Reading the file aborted."));
        }

    })
}

export function createRandomGradient() {
    const color = tinycolor.random();
    const fromColor = color.toRgbString();
    color.lighten();
    const toColor = color.toRgbString();
    return `linear-gradient(${fromColor}, ${toColor}`
}

export function createAvatarUrl(avatarId?: string, size?: number) {
    if (!avatarId) {
        return '/images/default-avatar.png';
    }

    if (size <= 30) {
        return shadowConfig.apiUrl + `/static/avatars/${size}x${size}/` + avatarId;
    }

    return shadowConfig.apiUrl + '/static/avatars/' + avatarId;
}

function generateRandomNumber(maxInteger = Number.MAX_SAFE_INTEGER) {
    return Math.round(Math.random() * maxInteger);
}

export function copyToClipboard(text: string) {
    if (navigator.clipboard) {
        return navigator.clipboard.writeText(text)
    } else {
        throw new Error("Clipboard API not available");
    }
}

export function createInviteLink(room?: Room, loginToken?: string, inviteToken?: string) {
    let base;

    if (inviteToken) {
        base = config.appUrl + `/invites/${inviteToken}`;
    } else if (room) {
        base = config.appUrl + `/rooms/${room.id}`;
    } else {
        base = config.appUrl;
    }

    if (loginToken) {
        return base + "?" + queryString.stringify({
            token: loginToken,
        });
    } else {
        return base;
    }

}


export function createMap(items: Array<{ id: string }>) {
    const map = new Map()
    for (const item of items) {
        map.set(item.id, item);
    }
    return map;
}


export default {
    copyToClipboard,
    shuffleArray,
    deNormalize,
    normalize,
    sortArrayObjects,
    sleep,
    throttle,
    createAvatarUrl,
    generateRandomNumber,
}
